import { useState } from "react";
import { errorNotification } from "@/shared/utils/errorNotification";
import { successNotification } from "@/shared/utils/successNotification";
import { useNavigate } from "react-router-dom";
import trpc from "@/shared/utils/trpc";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";
import { useUserContext } from "../../../context/UserContext";

export const useDashboardState = (mode: "create" | "edit" = "create") => {
  const navigate = useNavigate();
  const { userDetails, setUserDetails } = useUserContext();

  const [activeTab, setActiveTab] = useState<string | null>("company-profile");
  const [logo, setLogo] = useState<File | null>(null);
  const [aiImages, setAiImages] = useState<string[]>([]);
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [companyId, setCompanyId] = useState<string | null>(null);

  // Products & Services State
  const [products, setProducts] = useState<any[]>([]);
  const [services, setServices] = useState<any[]>([]);
  const [productErrors, setProductErrors] = useState<Record<number, { name?: string; description?: string; }>>({});
  const [serviceErrors, setServiceErrors] = useState<Record<number, { name?: string; description?: string; }>>({});
  const [productsToDelete, setProductsToDelete] = useState<string[]>([]);
  const [servicesToDelete, setServicesToDelete] = useState<string[]>([]);

  // Documents State (additional/optional company documents only)
  const [additionalDocs, setAdditionalDocs] = useState<any[]>([]); // New docs to upload
  const [existingDocs, setExistingDocs] = useState<any[]>([]); // Docs from DB
  const [documentsToDelete, setDocumentsToDelete] = useState<string[]>([]);

  const [companyStatus, setCompanyStatus] = useState<string | null>(null);
  const [companyGtid, setCompanyGtid] = useState<string | null>(null);
  const [isVerified, setIsVerified] = useState(false);
  const [isAdminBlocked, setIsAdminBlocked] = useState(false);
  const [existingLogo, setExistingLogo] = useState<any>(null);
  const [logoToDelete, setLogoToDelete] = useState<string | null>(null);
  const [existingImages, setExistingImages] = useState<any[]>([]);
  const [showIntroModal, setShowIntroModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  // Client ID generator
  const generateClientId = () => `client-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  const [formData, setFormData] = useState({
    // Personal Details
    userName: "",
    userDesignation: "",
    userPhoneNumber: "",
    userPhoneCountryCode: "",
    // Company Details
    companyLegalName: "",
    state: "",
    tradeName: "",
    country: "",
    registrationNumber: "",
    registrationIdType: null as string | null,
    yearOfIncorporation: null as Date | null,
    companyWebsite: "",
    companyEmail: "",
    phoneCountryCode: "",
    phoneNumber: "",
    companyDescription: "",
    industryCategory: [] as string[],
    subIndustry: [] as string[],
    whoAmI: [] as string[],
    collaborationIntent: [] as string[],
    coreCapabilities: [] as string[],
    secondaryCapabilities: [] as string[],
    keyDifferentiators: "",
    technologies: [] as string[],
    additionalTechInfrastructure: [] as string[],
    headquartersCountry: "",
    headquartersCity: "",
    currentAddress: "",
    operatingCountries: [] as string[],
    preferredRegions: [] as string[],
    companyRegisteredAddress: "",
    nationalIndustryClassificationCode: "",
    additionalCompanyStatus: "",
    directorName: "",
    directorDesignation: "",
    directorEmail: "",
    paidupCapitalBand: "" as string,
    turnoverBand: "" as string,
    verificationBusinessType: "" as string,
  });

  const [coreCapabilityInput, setCoreCapabilityInput] = useState("");
  const [secondaryCapabilityInput, setSecondaryCapabilityInput] = useState("");
  const [technologyInput, setTechnologyInput] = useState("");

  const updateForm = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const addTag = (field: "coreCapabilities" | "secondaryCapabilities" | "technologies", value: string) => {
    if (!value.trim()) return;
    const currentArray = formData[field];
    if (currentArray.length >= 10) return;
    if (!currentArray.includes(value.trim())) {
      updateForm(field, [...currentArray, value.trim()]);
    }
  };

  const removeTag = (field: "coreCapabilities" | "secondaryCapabilities" | "technologies", index: number) => {
    const currentArray = formData[field];
    updateForm(
      field,
      currentArray.filter((_, i) => i !== index)
    );
  };

  const removeAiImage = (index: number) => {
    setAiImages((prev) => prev.filter((_, i) => i !== index));
  };

  // Products Handlers
  const handleAddProduct = () => {
    setProducts((prev) => [...prev, { clientId: generateClientId(), name: "", description: "" }]);
  };

  const handleUpdateProduct = (index: number, field: string, value: any) => {
    setProducts((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
    if (productErrors[index]?.[field as keyof (typeof productErrors)[number]]) {
      setProductErrors((prev) => ({
        ...prev,
        [index]: { ...prev[index], [field]: undefined },
      }));
    }
  };

  const handleRemoveProduct = (index: number) => {
    const product = products[index];
    if (product.id) {
      setProductsToDelete((prev) => [...prev, product.id]);
    }
    setProducts((prev) => prev.filter((_, i) => i !== index));
    setProductErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[index];
      return newErrors;
    });
  };

  // Services Handlers
  const handleAddService = () => {
    setServices((prev) => [...prev, { clientId: generateClientId(), name: "", description: "" }]);
  };

  const handleUpdateService = (index: number, field: string, value: any) => {
    setServices((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
    if (serviceErrors[index]?.[field as keyof (typeof serviceErrors)[number]]) {
      setServiceErrors((prev) => ({
        ...prev,
        [index]: { ...prev[index], [field]: undefined },
      }));
    }
  };

  const handleRemoveService = (index: number) => {
    const service = services[index];
    if (service.id) {
      setServicesToDelete((prev) => [...prev, service.id]);
    }
    setServices((prev) => prev.filter((_, i) => i !== index));
    setServiceErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[index];
      return newErrors;
    });
  };

  const handleAddAdditionalDoc = () => {
    setAdditionalDocs((prev) => [...prev, { clientId: generateClientId(), name: "", file: null, url: "" }]);
  };

  const handleRemoveAdditionalDoc = (index: number) => {
    setAdditionalDocs((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUpdateAdditionalDoc = (index: number, field: string, value: any) => {
    setAdditionalDocs((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleDeleteExistingDoc = (docId: string) => {
    setDocumentsToDelete((prev) => [...prev, docId]);
    setExistingDocs((prev) => prev.filter((doc) => doc.id !== docId));
  };

  const handleLogoChange = async (file: File | null) => {
    setLogo(file);

    if (!file || !companyId) return;

    try {
      // If there's an existing logo, mark it for deletion
      if (existingLogo?.id) {
        setLogoToDelete(existingLogo.id);
      }

      const logoFormData = new FormData();
      logoFormData.append("file", file);
      logoFormData.append("category", "COMPANY_LOGO");
      logoFormData.append("companyId", companyId);

      const uploadedFile = await trpc.file.upload.mutate(logoFormData as any);

      if (uploadedFile?.id) {
        setExistingLogo(uploadedFile);
        setLogo(null);
        // Clear deletion flag since new logo replaced the old one
        setLogoToDelete(null);
      }
    } catch (error) {
      handleTrpcError(error);
    }
  };

  const handleRemoveLogo = () => {
    // Mark existing logo for deletion
    if (existingLogo?.id) {
      setLogoToDelete(existingLogo.id);
    }
    setLogo(null);
    setExistingLogo(null);
  };

  const handleSubmit = async () => {
    if (!companyId) {
      errorNotification({ title: "Error", message: "Company not found. Please try again." });
      return;
    }

    setIsSubmitting(true);

    try {
      const registrationIdType = formData.registrationIdType || null;
      const usesLei = registrationIdType === "LEI";

      const companyData: any = {
        legalName: formData.companyLegalName,
        tradeName: formData.tradeName || null,
        registrationNumber: usesLei
          ? formData.registrationNumber?.trim().toUpperCase() || null
          : formData.registrationNumber?.trim() || null,
        registrationIdType,
        yearOfIncorporation: formData.yearOfIncorporation
          ? typeof formData.yearOfIncorporation === "string"
            ? new Date(formData.yearOfIncorporation).getFullYear()
            : formData.yearOfIncorporation instanceof Date
              ? formData.yearOfIncorporation.getFullYear()
              : formData.yearOfIncorporation
          : null,
        state: formData.state || null,
        website: formData.companyWebsite || null,
        email: formData.companyEmail || null,
        phoneCountryCode: formData.phoneCountryCode || null,
        phoneNumber: formData.phoneNumber || null,
        description: formData.companyDescription || null,
        industryCategory: formData.industryCategory,
        subIndustry: formData.subIndustry || null,
        whoAmI: formData.whoAmI || [],
        collaborationIntent: formData.collaborationIntent || [],
        coreCapabilities: formData.coreCapabilities || [],
        secondaryCapabilities: formData.secondaryCapabilities || [],
        keyDifferentiators: formData.keyDifferentiators || null,
        technologies: formData.technologies || [],
        additionalTechInfrastructure: formData.additionalTechInfrastructure || [],
        headquartersCountry: formData.headquartersCountry || formData.country || "",
        headquartersCity: formData.headquartersCity || "",
        currentAddress: formData.currentAddress || null,
        operatingCountries: formData.operatingCountries || [],
        preferredCollaborationRegions: formData.preferredRegions || [],
      };

      const hasAnyAdditionalDetails =
        formData.companyRegisteredAddress?.trim() ||
        formData.nationalIndustryClassificationCode?.trim() ||
        formData.additionalCompanyStatus?.trim() ||
        formData.directorName?.trim() ||
        formData.directorEmail?.trim() ||
        formData.directorDesignation?.trim() ||
        formData.paidupCapitalBand?.trim() ||
        formData.turnoverBand?.trim() ||
        formData.verificationBusinessType?.trim();

      if (hasAnyAdditionalDetails) {
        companyData.additionalDetails = {
          companyRegisteredAddress: formData.companyRegisteredAddress?.trim() || null,
          nationalIndustryClassificationCode: formData.nationalIndustryClassificationCode?.trim() || null,
          companyStatus: formData.additionalCompanyStatus?.trim() || null,
          directorName: formData.directorName?.trim() || null,
          directorEmail: formData.directorEmail?.trim() || null,
          directorDesignation: formData.directorDesignation?.trim() || null,
          paidupCapitalBand: formData.paidupCapitalBand?.trim() || null,
          turnoverBand: formData.turnoverBand?.trim() || null,
          verificationBusinessType: formData.verificationBusinessType?.trim() || null,
        };
      }

      await trpc.company.update.mutate({ companyId, data: companyData });

      // Update User Personal Details
      await trpc.user.updateProfile.mutate({
        name: formData.userName?.trim() || undefined,
        designation: formData.userDesignation?.trim() || undefined,
        phoneNumber: formData.userPhoneNumber?.trim()
          ? `${formData.userPhoneCountryCode}${formData.userPhoneNumber}`.trim()
          : undefined,
      });

      // Save Products
      // Delete removed products
      for (const id of productsToDelete) {
        await trpc.productService.delete.mutate({ productServiceId: id });
      }
      // Create/Update products
      for (const product of products) {
        if (!product.name?.trim()) continue;
        if (product.id) {
          await trpc.productService.update.mutate({
            productServiceId: product.id,
            data: { name: product.name, description: product.description },
          });
        } else {
          await trpc.productService.create.mutate({
            companyId,
            type: "PRODUCT",
            name: product.name,
            description: product.description,
          });
        }
      }

      // Save Services
      // Delete removed services
      for (const id of servicesToDelete) {
        await trpc.productService.delete.mutate({ productServiceId: id });
      }
      // Create/Update services
      for (const service of services) {
        if (!service.name?.trim()) continue;
        if (service.id) {
          await trpc.productService.update.mutate({
            productServiceId: service.id,
            data: { name: service.name, description: service.description },
          });
        } else {
          await trpc.productService.create.mutate({
            companyId,
            type: "SERVICE",
            name: service.name,
            description: service.description,
          });
        }
      }

      // 2. Delete removed logo from S3
      if (logoToDelete) {
        await trpc.file.delete.mutate({ fileId: logoToDelete });
        setLogoToDelete(null);
      }

      // 3. Delete removed documents
      for (const id of documentsToDelete) {
        await trpc.file.delete.mutate({ fileId: id });
      }

      // 3. Save additional documents (new ones)
      for (const doc of additionalDocs) {
        if (!doc.name?.trim()) continue;
        if (doc.file) {
          const formData = new FormData();
          formData.append("file", doc.file);
          formData.append("category", "COMPANY_DOCUMENT");
          formData.append("companyId", companyId);
          formData.append("name", doc.name);
          await trpc.file.upload.mutate(formData as any);
        } else if (doc.url) {
          await trpc.file.saveDocumentUrl.mutate({
            url: doc.url,
            category: "COMPANY_DOCUMENT",
            companyId,
            name: doc.name,
          });
        }
      }

      // Download and upload AI generated images from external URLs to our S3 bucket
      for (const imageUrl of aiImages) {
        try {
          // Fetch the image from the presigned URL
          const response = await fetch(imageUrl);
          if (!response.ok) {
            console.error(`Failed to fetch AI image from ${imageUrl}`);
            continue;
          }

          // Convert to blob
          const blob = await response.blob();

          // Get mime type from blob or response headers
          const mimeType = blob.type || response.headers.get("content-type") || "image/jpeg";

          // Map mime type to file extension
          const getExtensionFromMimeType = (mime: string): string => {
            const mimeMap: Record<string, string> = {
              "image/jpeg": "jpg",
              "image/jpg": "jpg",
              "image/png": "png",
              "image/gif": "gif",
              "image/webp": "webp",
              "image/svg+xml": "svg",
              "image/bmp": "bmp",
              "image/tiff": "tiff",
            };
            return mimeMap[mime.toLowerCase()] || "jpg";
          };

          const extension = getExtensionFromMimeType(mimeType);

          // Extract filename from URL or generate one
          const urlPath = new URL(imageUrl).pathname;
          let filename = urlPath.split("/").pop() || `ai-generated-${Date.now()}.${extension}`;

          // Ensure filename has correct extension matching the mime type
          const hasCorrectExtension = filename.toLowerCase().endsWith(`.${extension}`);
          if (!hasCorrectExtension) {
            // Remove existing extension if any and add correct one
            filename = filename.replace(/\.[^.]+$/, "") + `.${extension}`;
          }

          // Create a File object from the blob with the correct mime type
          const file = new File([blob], filename, { type: mimeType });

          // Upload to our S3 bucket
          const imageFormData = new FormData();
          imageFormData.append("file", file);
          imageFormData.append("category", "ADDITIONAL_IMAGES");
          imageFormData.append("companyId", companyId);
          await trpc.file.upload.mutate(imageFormData as any);
        } catch (error) {
          console.error(`Error uploading AI image from ${imageUrl}:`, error);
          // Continue with other images even if one fails
        }
      }

      const updatedProfile = await trpc.user.getProfile.query({});
      if (updatedProfile) setUserDetails(updatedProfile as any);

      successNotification({
        title: "Success",
        message: mode === "edit" ? "Profile updated successfully!" : "Profile created successfully!",
      });

      setProductsToDelete([]);
      setServicesToDelete([]);
      setDocumentsToDelete([]);

      setAdditionalDocs([]);

      setLogo(null);
      setAiImages([]);
      navigate("/profile");
    } catch (err) {
      handleTrpcError(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid = () => {
    // Personal Details - Required fields
    const hasUserName = formData.userName?.trim().length > 0;
    const hasUserDesignation = formData.userDesignation?.trim().length > 0;

    // Basic Company Information - Required fields
    const hasCompanyLegalName = formData.companyLegalName?.trim().length > 0;
    const hasCountry = formData.country?.trim().length > 0;
    const hasRegistrationIdValue = !!formData.registrationNumber?.trim().length;
    const hasCompanyWebsite = formData.companyWebsite?.trim().length > 0;
    const hasCompanyEmail = formData.companyEmail?.trim().length > 0;
    const hasPhoneNumber = formData.phoneNumber?.trim().length > 0;

    // Business Overview - Required fields
    const hasCompanyDescription = formData.companyDescription?.trim().length > 0;
    const hasIndustryCategory = formData.industryCategory.length > 0;
    const hasWhoAmI = formData.whoAmI.length > 0;

    // Capabilities & Differentiators - Required fields
    const hasCoreCapabilities = formData.coreCapabilities.length > 0;

    // Geography & Markets - Required fields
    const hasHeadquartersCountry =
      formData.headquartersCountry?.trim().length > 0 || formData.country?.trim().length > 0;
    const hasHeadquartersCity = formData.headquartersCity?.trim().length > 0;

    const hq =
      formData.headquartersCountry?.trim().length > 0 || formData.country?.trim().length > 0;
    const hasRegistrationIdType = !hq || !!formData.registrationIdType;

    // Base validation (always required)
    const baseValidation =
      hasUserName &&
      hasUserDesignation &&
      hasCompanyLegalName &&
      hasCountry &&
      hasRegistrationIdValue &&
      hasRegistrationIdType &&
      hasCompanyWebsite &&
      hasCompanyEmail &&
      hasPhoneNumber &&
      hasCompanyDescription &&
      hasIndustryCategory &&
      hasWhoAmI &&
      hasCoreCapabilities &&
      hasHeadquartersCountry &&
      hasHeadquartersCity;

    // In edit mode, only base validation is required
    if (mode === "edit") {
      // Also Validation for products and services
      const areProductsValid = products.every((p) => p.name?.trim()?.length > 0 && p.description?.trim()?.length > 0);
      const areServicesValid = services.every((s) => s.name?.trim()?.length > 0 && s.description?.trim()?.length > 0);
      const areAdditionalDocsValid = additionalDocs.every(
        (doc) => doc.name?.trim()?.length > 0 && (doc.file || doc.url?.trim()?.length > 0)
      );

      return baseValidation && areProductsValid && areServicesValid && areAdditionalDocsValid;
    }

    // Also Validation for products and services
    const areProductsValid = products.every((p) => p.name?.trim()?.length > 0 && p.description?.trim()?.length > 0);
    const areServicesValid = services.every((s) => s.name?.trim()?.length > 0 && s.description?.trim()?.length > 0);
    const areAdditionalDocsValid = additionalDocs.every(
      (doc) => doc.name?.trim()?.length > 0 && (doc.file || doc.url?.trim()?.length > 0)
    );

    return (
      baseValidation &&
      areProductsValid &&
      areServicesValid &&
      areAdditionalDocsValid
    );
  };

  return {
    // State
    activeTab,
    setActiveTab,
    logo,
    setLogo,
    aiImages,
    setAiImages,
    isPageLoading,
    setIsPageLoading,
    isSubmitting,
    companyId,
    setCompanyId,
    companyStatus,
    setCompanyStatus,
    companyGtid,
    setCompanyGtid,
    isVerified,
    setIsVerified,
    isAdminBlocked,
    setIsAdminBlocked,
    existingLogo,
    setExistingLogo,
    existingImages,
    setExistingImages,
    showIntroModal,
    setShowIntroModal,
    showCreateModal,
    setShowCreateModal,
    isGenerating,
    setIsGenerating,
    formData,
    setFormData,
    coreCapabilityInput,
    setCoreCapabilityInput,
    secondaryCapabilityInput,
    setSecondaryCapabilityInput,
    technologyInput,
    setTechnologyInput,
    userDetails,
    // Handlers
    updateForm,
    addTag,
    removeTag,
    removeAiImage,
    handleSubmit,
    handleLogoChange,
    handleRemoveLogo,
    // Products & Services Handlers
    products,
    setProducts,
    services,
    setServices,
    productErrors,
    serviceErrors,
    handleAddProduct,
    handleUpdateProduct,
    handleRemoveProduct,
    handleAddService,
    handleUpdateService,
    handleRemoveService,
    // Documents Handlers
    additionalDocs,
    setAdditionalDocs,
    existingDocs,
    setExistingDocs,
    handleAddAdditionalDoc,
    handleRemoveAdditionalDoc,
    handleUpdateAdditionalDoc,
    handleDeleteExistingDoc,
    isFormValid: isFormValid(),
    invalidFields: (() => {
      const errors: Record<string, string[]> = {
        "company-profile": [],
        images: [],
      };

      // Company Profile Tab
      if (!formData.userName?.trim()) errors["company-profile"].push("Name");
      if (!formData.userDesignation?.trim()) errors["company-profile"].push("Designation");
      if (!formData.companyLegalName?.trim()) errors["company-profile"].push("Company Legal Name");
      if (!formData.country?.trim()) errors["company-profile"].push("Country");
      const normalizedCountry = (formData.headquartersCountry || formData.country || "").trim().toLowerCase();
      const isUsCountry = normalizedCountry === "usa" || normalizedCountry === "united states";
      const hqSelected =
        !!(formData.country?.trim() || formData.headquartersCountry?.trim());
      if (hqSelected && !formData.registrationIdType) {
        errors["company-profile"].push("Registration ID Type");
      }
      if (isUsCountry && !formData.state?.trim()) errors["company-profile"].push("State");
      if (formData.registrationIdType === "LEI") {
        if (!formData.registrationNumber?.trim()) errors["company-profile"].push("LEI");
      } else if (hqSelected && formData.registrationIdType && !formData.registrationNumber?.trim()) {
        errors["company-profile"].push("Registration Number");
      }
      if (!formData.companyWebsite?.trim()) errors["company-profile"].push("Company Website");
      if (!formData.companyEmail?.trim()) errors["company-profile"].push("Company Email");
      if (!formData.phoneNumber?.trim()) errors["company-profile"].push("Phone Number");
      if (!formData.companyDescription?.trim()) errors["company-profile"].push("Company Description");
      if (formData.industryCategory.length === 0) errors["company-profile"].push("Industry Category");
      if (formData.whoAmI.length === 0) errors["company-profile"].push("Who Am I?");
      if (formData.coreCapabilities.length === 0) errors["company-profile"].push("Core Capabilities");
      if (!formData.headquartersCountry?.trim() && !formData.country?.trim())
        errors["company-profile"].push("Headquarters Country");
      if (!formData.headquartersCity?.trim()) errors["company-profile"].push("Headquarters City");

      // Products & Services (merged into Company Profile tab)
      products.forEach((p, index) => {
        if (!p.name?.trim()) errors["company-profile"].push(`Product ${index + 1} Name`);
        if (!p.description?.trim()) errors["company-profile"].push(`Product ${index + 1} Description`);
      });
      services.forEach((s, index) => {
        if (!s.name?.trim()) errors["company-profile"].push(`Service ${index + 1} Name`);
        if (!s.description?.trim()) errors["company-profile"].push(`Service ${index + 1} Description`);
      });

      additionalDocs.forEach((doc, index) => {
        if (!doc.name?.trim()) errors["images"].push(`Additional Doc ${index + 1} Name`);
        if (!doc.file && !doc.url?.trim()) errors["images"].push(`Additional Doc ${index + 1} File`);
      });

      return errors;
    })(),
  };
};
